/**
 * Document download helpers — `.docx`, `.pdf`, `.txt`.
 *
 * All exported functions take a Markdown string + a base file name and
 * return a Promise that resolves once the browser download has been
 * triggered.
 *
 * `.docx` is built from the Markdown via the `docx` library so paragraphs,
 * headings, bold, and horizontal-rule dividers are preserved.
 *
 * `.pdf` is generated via `html2pdf.js`. We render the Markdown to HTML
 * first (using a tiny home-grown converter so we don't drag the React
 * tree into the call site), then hand the HTML node to html2pdf.
 */
import { saveAs } from 'file-saver';
import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType, PageBreak,
} from 'docx';

/**
 * Generate a content-aware file name for downloads.
 *
 *   NDA between **Bapco Energies** and **Saudi Aramco** → "NDA_Bapco-Energies_Saudi-Aramco"
 *   اتفاقية سرية بين **شركة ألفا** و**شركة بيتا**       → "NDA_شركة-ألفا_شركة-بيتا"
 *   Otherwise                                          → "<DocType>_<YYYY-MM-DD>"
 *
 * Caller passes the rendered Markdown content + the contract type id
 * (e.g. "nda", "employment_contract"). We use the same file name across
 * `.txt`, `.docx` and `.pdf` so all three exports stay aligned.
 */
const DOC_TYPE_NAMES = {
  nda: 'NDA',
  employment: 'Employment-Contract',
  employment_contract: 'Employment-Contract',
  partnership: 'Partnership-Agreement',
  partnership_agreement: 'Partnership-Agreement',
  commercial: 'Commercial-Contract',
  commercial_contract: 'Commercial-Contract',
  service: 'Service-Agreement',
  service_agreement: 'Service-Agreement',
  termination: 'Termination-Letter',
  compliance: 'Compliance-Document',
  policy: 'Internal-Policy',
};

export function generateFilename(markdownContent = '', contractType = null) {
  const text = markdownContent || '';
  // Try to extract two party names from common contract preamble formats.
  // Patterns we look for (Markdown bold preserves the names):
  //   between **A** and **B**              (English)
  //   بين **A** و**B**  / بين **A** و **B**  (Arabic)
  const enMatch = text.match(/\bbetween\s+\*\*([^*]+)\*\*\s+and\s+\*\*([^*]+)\*\*/i);
  const arMatch = text.match(/بين\s*\*\*([^*]+)\*\*\s*و\s*\*\*([^*]+)\*\*/);
  const partyMatch = enMatch || arMatch;

  const typeName = DOC_TYPE_NAMES[contractType] || 'Legal-Document';

  if (partyMatch) {
    const tidy = (s) => (s || '')
      .trim()
      .replace(/[\n\r\t]/g, ' ')
      .replace(/[\\/:*?"<>|]/g, '')   // strip filesystem-illegal chars
      .replace(/\s+/g, '-')
      .slice(0, 24);
    const a = tidy(partyMatch[1]);
    const b = tidy(partyMatch[2]);
    if (a && b) return `${typeName}_${a}_${b}`;
  }

  const date = new Date().toISOString().split('T')[0];
  return `${typeName}_${date}`;
}

/* ── Markdown → DOCX ──────────────────────────────────────────────── */



/**
 * Tokenise a Markdown line into bold + plain `TextRun`s.
 *  - `**bold**` → bold run
 *  - rest stays plain
 */
function lineToRuns(line, { isArabic }) {
  const runs = [];
  const re = /\*\*(.+?)\*\*/g;
  let cursor = 0;
  let m;
  while ((m = re.exec(line)) !== null) {
    if (m.index > cursor) {
      runs.push(new TextRun({ text: line.slice(cursor, m.index), font: 'Cairo', rightToLeft: isArabic }));
    }
    runs.push(new TextRun({ text: m[1], bold: true, font: 'Cairo', rightToLeft: isArabic }));
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) {
    runs.push(new TextRun({ text: line.slice(cursor), font: 'Cairo', rightToLeft: isArabic }));
  }
  return runs.length ? runs : [new TextRun({ text: line, font: 'Cairo', rightToLeft: isArabic })];
}

function markdownToParagraphs(markdown, { isArabic }) {
  const lines = (markdown || '').split('\n');
  const out = [];
  for (const raw of lines) {
    const line = raw.replace(/\r$/, '');

    // Horizontal rule
    if (/^\s*---+\s*$/.test(line)) {
      out.push(new Paragraph({
        children: [new TextRun('────────────────────────────────────────────')],
        alignment: AlignmentType.CENTER,
      }));
      continue;
    }

    // Page break (\f marker — we don't emit these from our backend yet)
    if (line === '\f') {
      out.push(new Paragraph({ children: [new PageBreak()] }));
      continue;
    }

    // Headings
    const h1 = /^# +(.+)$/.exec(line);
    const h2 = /^## +(.+)$/.exec(line);
    const h3 = /^### +(.+)$/.exec(line);
    if (h1) {
      out.push(new Paragraph({
        heading: HeadingLevel.HEADING_1,
        bidirectional: isArabic,
        alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
        children: lineToRuns(h1[1], { isArabic }),
      }));
      continue;
    }
    if (h2) {
      out.push(new Paragraph({
        heading: HeadingLevel.HEADING_2,
        bidirectional: isArabic,
        alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
        children: lineToRuns(h2[1], { isArabic }),
      }));
      continue;
    }
    if (h3) {
      out.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        bidirectional: isArabic,
        alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
        children: lineToRuns(h3[1], { isArabic }),
      }));
      continue;
    }

    // Bullet
    const bullet = /^\s*[-*+] +(.+)$/.exec(line);
    if (bullet) {
      out.push(new Paragraph({
        bullet: { level: 0 },
        bidirectional: isArabic,
        alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
        children: lineToRuns(bullet[1], { isArabic }),
      }));
      continue;
    }

    // Numbered list
    const num = /^\s*\d+\.\s+(.+)$/.exec(line);
    if (num) {
      out.push(new Paragraph({
        numbering: { reference: 'numbered', level: 0 },
        bidirectional: isArabic,
        alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
        children: lineToRuns(num[1], { isArabic }),
      }));
      continue;
    }

    // Plain paragraph (or empty line for spacing)
    out.push(new Paragraph({
      bidirectional: isArabic,
      alignment: isArabic ? AlignmentType.RIGHT : AlignmentType.LEFT,
      children: lineToRuns(line, { isArabic }),
    }));
  }
  return out;
}

/**
 * Generate a `.docx` from Markdown content and trigger download.
 * Returns the blob (caller can ignore).
 */
export async function downloadDocx(markdown, fileName = 'document', { isArabic = true } = {}) {
  const doc = new Document({
    creator: 'Bayenah',
    title: fileName,
    styles: {
      default: {
        document: { run: { font: 'Cairo' } },
      },
    },
    sections: [{
      properties: {
        page: { margin: { top: 1100, bottom: 1100, left: 1100, right: 1100 } },
      },
      children: markdownToParagraphs(markdown, { isArabic }),
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${sanitizeFileName(fileName)}.docx`);
  return blob;
}

/* ── Markdown → PDF (via HTML) ────────────────────────────────────── */

/** Tiny Markdown → HTML converter (handles the subset we emit). */
function markdownToHtml(markdown) {
  if (!markdown) return '';
  // Escape HTML first so user content doesn't break.
  let html = markdown
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  // Horizontal rule
  html = html.replace(/^\s*---+\s*$/gm, '<hr/>');
  // Bullets
  html = html.replace(/^\s*[-*+] (.+)$/gm, '<li>$1</li>');
  // Wrap consecutive <li>s in <ul>
  html = html.replace(/(?:<li>.*<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`);
  // Paragraphs (consecutive non-tag, non-empty lines)
  html = html.split(/\n{2,}/).map((block) => {
    if (/^<(h\d|ul|ol|hr|li)/.test(block.trim())) return block;
    if (!block.trim()) return '';
    return `<p>${block.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');

  return html;
}

/**
 * Generate a `.pdf` from Markdown content via the browser's native
 * print-to-PDF flow. We open a popup window (or fall back to a hidden
 * iframe when popups are blocked), write the rendered HTML styled for
 * print, and trigger `window.print()`. Users save the PDF via the
 * browser's "Save as PDF" destination.
 *
 * Why this approach: html2pdf + html2canvas had silent failures on
 * off-screen containers (blank PDFs). The browser's native print engine
 * is rock-solid for legal documents and produces selectable, copy-able
 * text — which html2canvas cannot (it rasterizes to image).
 */
export async function downloadPdf(markdown, fileName = 'document', { isArabic = true, title = '' } = {}) {
  const html = markdownToHtml(markdown);
  // eslint-disable-next-line no-console
  console.log('[PDF] Content length:', (markdown || '').length, '| HTML length:', html.length);
  if (!html.trim()) {
    throw new Error('PDF generation aborted — empty document content');
  }
  const safeName = sanitizeFileName(fileName);
  const dir = isArabic ? 'rtl' : 'ltr';
  const fontStack = isArabic
    ? "'Cairo', 'IBM Plex Sans Arabic', sans-serif"
    : "'Times New Roman', Georgia, serif";

  const docHtml = `<!DOCTYPE html>
<html dir="${dir}" lang="${isArabic ? 'ar' : 'en'}">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(safeName)}</title>
  <style>
    @page { size: A4; margin: 22mm 20mm; }
    @media print {
      .no-print { display: none !important; }
    }
    html, body { background: #FFFFFF; color: #000000; }
    body {
      font-family: ${fontStack};
      font-size: 12pt;
      line-height: 1.7;
      margin: 0;
      padding: 0;
      direction: ${dir};
      ${isArabic ? 'text-align: right;' : 'text-align: left;'}
    }
    h1 { font-size: 18pt; font-weight: 700; text-align: center; margin: 0 0 24pt; }
    h2 { font-size: 13pt; font-weight: 700; margin: 18pt 0 8pt; }
    h3 { font-size: 12pt; font-weight: 700; margin: 12pt 0 6pt; }
    p  { margin: 0 0 8pt; }
    ul, ol { margin: 8pt 24pt; padding: 0; }
    li { margin: 0 0 4pt; }
    hr { border: none; border-top: 1px solid #000; margin: 16pt 0; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    .doc-title { font-size: 18pt; font-weight: 700; text-align: center; margin: 0 0 24pt; }
    .toolbar { padding: 12px 16px; border-bottom: 1px solid #DDD; background: #F8F8F8;
      display: flex; gap: 8px; justify-content: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    .toolbar button {
      background: #111; color: #FFF; border: 0; padding: 8px 18px;
      border-radius: 9999px; font-size: 13px; cursor: pointer;
    }
    .toolbar .secondary { background: #FFF; color: #111; border: 1px solid #CCC; }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">${isArabic ? 'حفظ كـ PDF' : 'Save as PDF'}</button>
    <button class="secondary" onclick="window.close()">${isArabic ? 'إغلاق' : 'Close'}</button>
  </div>
  <div style="padding: 24pt;">
    ${title ? `<div class="doc-title">${escapeHtml(title)}</div>` : ''}
    ${html}
  </div>
  <script>
    // Auto-trigger print on load — the user picks "Save as PDF" as the
    // destination. If the user cancels, the toolbar buttons remain.
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 350);
    });
  </script>
</body>
</html>`;

  // Try a popup window first. Some browsers (and some test runners) block
  // popups — fall back to a hidden iframe in that case. The iframe path
  // works on Chrome/Edge/Safari/Firefox; iOS Safari is the lone holdout
  // where window.print() in an off-screen iframe is a no-op — for that
  // edge case the popup MUST succeed, so we surface a clear message
  // when it doesn't.
  let printWindow = null;
  try { printWindow = window.open('', '_blank', 'width=900,height=1100'); } catch (_) { printWindow = null; }
  const popupBlocked = !printWindow || printWindow.closed || typeof printWindow.closed === 'undefined';
  if (printWindow && !popupBlocked && printWindow.document) {
    printWindow.document.open();
    printWindow.document.write(docHtml);
    printWindow.document.close();
    printWindow.focus();
    return;
  }

  // Popup-blocked fallback: hidden same-origin iframe. We still try it
  // because it works for the vast majority of users — but we ALSO warn
  // in the console so a debugging user understands why the second tab
  // didn't appear, and we surface a non-blocking alert if even the
  // iframe path is unreachable (no DOM, sandbox CSP, etc.).
  // eslint-disable-next-line no-console
  console.warn('[PDF] popup blocked, falling back to hidden-iframe print');
  if (typeof document === 'undefined' || !document.body) {
    // eslint-disable-next-line no-alert
    alert(
      isArabic
        ? 'يرجى السماح بالنوافذ المنبثقة من bayenah.com لتنزيل ملف PDF.'
        : 'Please allow pop-ups for bayenah.com to download as PDF. Click the pop-up blocked icon in your browser address bar.'
    );
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument || iframe.contentWindow.document;
  idoc.open();
  idoc.write(docHtml);
  idoc.close();
  // Print after layout settles. Remove iframe shortly after.
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) { /* best-effort */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) { /* noop */ } }, 4000);
  }, 400);
}

function escapeHtml(str) {
  return (str || '').toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/* ── Plain text ──────────────────────────────────────────────────── */

export function downloadTxt(markdown, fileName = 'document') {
  const blob = new Blob([markdown || ''], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, `${sanitizeFileName(fileName)}.txt`);
  return blob;
}

function sanitizeFileName(name) {
  return (name || 'document').toString().replace(/[^\w\u0600-\u06FF\-_]+/g, '_').slice(0, 80) || 'document';
}

export default { downloadDocx, downloadPdf, downloadTxt };
